/*
 * custom-doppio.js
 * ================
 * Doppio-spezifische Web-Components und Event-Hooks.
 * Wird via type="module" geladen, nutzt Impact's exportierte ES-Modules.
 *
 * Architektur:
 *   1. Reveal-Observer (Scroll-In-View, kein JS-Framework)
 *   2. DoppioCartDrawer (extends CartDrawer) — Free-Shipping + Sample-Cross-Sell
 *   3. DoppioAboToggle — Selling-Plan-Switcher auf PDP
 *   4. DoppioGrindSelector — Brüh-Methoden-Auswahl mit Variant-Sync
 *   5. DoppioShippingBanner — schliessbar (Cookie-persistiert)
 *
 * Update-Safe: dieser File bleibt bei Maestrooo-Updates erhalten.
 * Keine Imports von Impact-Internals — Kommunikation läuft über Events.
 */

/* ============================================
   Konfiguration (an einer Stelle, leicht änderbar)
   ============================================ */
const DOPPIO = Object.freeze({
  freeShippingThresholdCents: 5000,   // 50 € (Doppio-Live-Stand)
  sampleEnabled: false,               // Cross-Sell-Gratisprobe AUS — Cascara aktuell nicht verfügbar. Auf true setzen, sobald ein Probe-Produkt verfügbar ist.
  sampleProductHandle: 'cascara-kaffeekirschentee-50g', // Cross-Sell-Default
  sampleTriggerCents: 3000,           // ab 30 € im Cart aktivieren
  storageKeys: {
    shippingBannerDismissed: 'doppio:shipping-banner-dismissed',
  },
});

const fmtMoney = (cents) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);

/* ============================================
   -2. Header-Group Gap-Killer (forced inline-style override)
   Setzt margin/border/padding via JS auf 0, weil !important-CSS
   teilweise von Impact's internem CSS gewinnt.
   ============================================ */
(() => {
  const killGaps = () => {
    const group = document.querySelector('.shopify-section-group-header-group');
    if (!group) return;
    group.style.setProperty('display', 'flex', 'important');
    group.style.setProperty('flex-direction', 'column', 'important');
    group.style.setProperty('gap', '0', 'important');
    group.style.setProperty('row-gap', '0', 'important');

    group.querySelectorAll(':scope > *, .shopify-section, [class*="shopify-section--"]').forEach((el) => {
      el.style.setProperty('margin-top', '0', 'important');
      el.style.setProperty('margin-bottom', '0', 'important');
      el.style.setProperty('margin-block', '0', 'important');
      el.style.setProperty('border-top-width', '0', 'important');
      el.style.setProperty('border-bottom-width', '0', 'important');
      el.style.setProperty('border-block-width', '0', 'important');
      el.style.setProperty('outline', '0', 'important');
      el.style.setProperty('background-color', 'transparent', 'important');
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', killGaps);
  } else {
    killGaps();
  }
  document.addEventListener('shopify:section:load', killGaps);
  document.addEventListener('shopify:section:unload', killGaps);
  // Run again after a tick in case Impact re-renders
  setTimeout(killGaps, 100);
  setTimeout(killGaps, 500);
})();

/* ============================================
   -1. Co-Branding Header-Logo
   Ersetzt das Impact-Header-Logo (.header__logo-image) durch das
   komplette DOPPIO × HSV Co-Brand-Lockup-Bild aus theme/assets/.
   Funktioniert auf ALLEN Pages.
   ============================================ */
(() => {
  const apply = () => {
    const tpl = document.getElementById('doppio-cobrand-logo-template');
    if (!tpl) return;
    const lockup = tpl.content.querySelector('img');
    if (!lockup) return;
    const newSrc = lockup.getAttribute('src');
    const newAlt = lockup.getAttribute('alt');
    if (!newSrc) return;

    const headerImages = document.querySelectorAll('.header__logo-image, .header__logo img');
    if (!headerImages.length) return;
    headerImages.forEach((img) => {
      if (img.dataset.doppioCobrandApplied === '1') return;
      img.dataset.doppioCobrandApplied = '1';
      // Ensure srcset doesn't override
      img.removeAttribute('srcset');
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
      img.src = newSrc;
      if (newAlt) img.alt = newAlt;
      img.classList.add('doppio-cobrand-logo-img');
    });

    const logoWrap = document.querySelector('.header__logo');
    if (logoWrap) logoWrap.classList.add('doppio-cobrand-host');
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply);
  } else {
    apply();
  }
  document.addEventListener('shopify:section:load', apply);
})();

/* ============================================
   -0.6. Policy-URL-Redirect & Link-Rewrite
   ============================================
   Shopify-System-Policies (/policies/legal-notice etc.) bleiben oft leer
   weil Aydin im Admin keine Inhalte pflegt. Wir bauen stattdessen
   gleichwertige /pages/* — und leiten:
    a) wenn jemand direkt auf /policies/* landet → sofort redirect auf /pages/*
    b) alle Footer/Link-list-Anchors mit /policies/* → href umschreiben
*/
(() => {
  const MAP = {
    'legal-notice': '/pages/impressum',
    'privacy-policy': '/pages/datenschutz',
    'terms-of-service': '/pages/agb',
    'shipping-policy': '/pages/versand',
    'refund-policy': '/pages/widerruf',
  };

  // a) Direct-URL-Redirect (sofort, bevor das DOM lädt)
  const path = window.location.pathname;
  const match = path.match(/^\/policies\/([a-z0-9-]+)\/?$/);
  if (match && MAP[match[1]]) {
    window.location.replace(MAP[match[1]] + window.location.search + window.location.hash);
    return; // Stop rest of IIFE
  }

  // b) Link-Rewrite (auf jeder Page)
  const rewriteLinks = () => {
    document.querySelectorAll('a[href*="/policies/"]').forEach((a) => {
      const href = a.getAttribute('href') || '';
      const m = href.match(/\/policies\/([a-z0-9-]+)/);
      if (m && MAP[m[1]]) {
        const newHref = href.replace(/\/policies\/[a-z0-9-]+/, MAP[m[1]]);
        a.setAttribute('href', newHref);
        a.setAttribute('data-doppio-rewritten', '1');
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', rewriteLinks);
  } else {
    rewriteLinks();
  }
  // Re-run wenn Footer/Sections async nachgeladen werden
  const obs = new MutationObserver(rewriteLinks);
  obs.observe(document.body || document.documentElement, { childList: true, subtree: true });
  setTimeout(() => obs.disconnect(), 5000);
})();

/* ============================================
   -0.5. WP-05 — Menüpunkt "Kooperation" injizieren
   Hängt sich an die Header-Navigation und fügt einen Link zwischen
   Geschäftskunden und Kontakt ein. Ueberbrueckt die fehlende
   Linklist-Pflege im Shopify-Admin. Funktioniert auf JEDER Page.
   Idempotent: prüft via data-attribute ob schon eingefügt.
   ============================================ */
(() => {
  const KOOPERATION = {
    label: 'Kooperation',
    href: '/pages/hsv',
    afterLinkContains: ['geschäftskunden', 'geschaeftskunden', 'business', 'b2b'],
    beforeLinkContains: ['kontakt', 'contact'],
  };

  const tryInject = () => {
    if (document.querySelector('[data-doppio-kooperation]')) return true;

    const navs = document.querySelectorAll(
      '.header__main-nav, .header__menu, .header__link-list, nav[role="navigation"], .mega-menu, .header [role="menubar"]'
    );

    for (const nav of navs) {
      const links = Array.from(nav.querySelectorAll('a'));
      if (links.length === 0) continue;

      const findLink = (needles) =>
        links.find((a) => {
          const t = (a.textContent || '').toLowerCase().trim();
          const h = (a.getAttribute('href') || '').toLowerCase();
          return needles.some((n) => t.includes(n) || h.includes(n));
        });

      // Falls bereits ein "Kooperation"-Link existiert (Admin-Linklist) → skip
      const existing = links.find((a) => /kooperation/i.test((a.textContent || '').trim()));
      if (existing) {
        existing.setAttribute('data-doppio-kooperation', '1');
        return true;
      }

      const before = findLink(KOOPERATION.beforeLinkContains);
      const after = findLink(KOOPERATION.afterLinkContains);
      if (!before && !after) continue;

      const template = (after || before).cloneNode(true);
      template.setAttribute('data-doppio-kooperation', '1');
      template.setAttribute('href', KOOPERATION.href);

      const setLabel = (node) => {
        for (const child of node.childNodes) {
          if (child.nodeType === Node.TEXT_NODE && child.textContent.trim()) {
            child.textContent = KOOPERATION.label;
            return true;
          }
          if (child.nodeType === Node.ELEMENT_NODE && setLabel(child)) return true;
        }
        return false;
      };
      setLabel(template);

      const linkParent = (after || before).parentElement;
      let nodeToInsert = template;
      if (linkParent && linkParent.tagName === 'LI') {
        const liClone = linkParent.cloneNode(false);
        liClone.appendChild(template);
        nodeToInsert = liClone;
      }

      if (before) {
        const beforeParent = before.parentElement;
        const insertTarget = beforeParent && beforeParent.tagName === 'LI' ? beforeParent : before;
        insertTarget.parentElement.insertBefore(nodeToInsert, insertTarget);
        return true;
      }
      if (after) {
        const afterParent = after.parentElement;
        const insertTarget = afterParent && afterParent.tagName === 'LI' ? afterParent : after;
        insertTarget.parentElement.insertBefore(nodeToInsert, insertTarget.nextSibling);
        return true;
      }
    }
    return false;
  };

  const init = () => {
    if (tryInject()) return;
    const obs = new MutationObserver(() => {
      if (tryInject()) obs.disconnect();
    });
    const target = document.querySelector('.shopify-section-group-header-group') || document.body;
    if (target) obs.observe(target, { childList: true, subtree: true });
    setTimeout(() => obs.disconnect(), 3000);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();

/* ============================================
   0. Transparent-Header (global, alle Pages)
   - Top: dark-gradient (lesbar auf hellen UND dunklen Backgrounds)
   - Scroll > 60px: solid Navy + Blur
   - Header-Height wird als CSS-Variable gemessen
     (für <main> padding-top auf Sub-Pages)
   ============================================ */
(() => {
  let raf = null;
  const TH = 60;

  const updateState = () => {
    document.body.classList.toggle('is-scrolled', window.scrollY > TH);
  };

  const updateHeaderHeight = () => {
    const headerGroup = document.querySelector('.shopify-section-group-header-group');
    if (!headerGroup) return;
    const h = headerGroup.offsetHeight;
    document.documentElement.style.setProperty('--doppio-header-total-height', `${h}px`);
    document.documentElement.style.setProperty('--doppio-header-height', `${h}px`);
  };

  const onScroll = () => {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(updateState);
  };

  updateState();
  updateHeaderHeight();

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', updateHeaderHeight, { passive: true });
  // Re-measure when sections load (theme editor)
  document.addEventListener('shopify:section:load', updateHeaderHeight);
  document.addEventListener('shopify:section:unload', updateHeaderHeight);
})();

/* ============================================
   1. Reveal-Observer — Sektionen einfaden beim Scrollen
   ============================================ */
(() => {
  if (typeof IntersectionObserver === 'undefined') return;
  const obs = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          obs.unobserve(e.target);
        }
      });
    },
    { threshold: 0.1, rootMargin: '0px 0px -8% 0px' }
  );
  const observe = () =>
    document.querySelectorAll('.doppio-reveal:not(.is-visible)').forEach((el) => obs.observe(el));
  observe();
  document.addEventListener('shopify:section:load', observe);
})();

/* ============================================
   2. Shipping-Banner — schliessbar, persistiert
   ============================================ */
class DoppioShippingBanner extends HTMLElement {
  connectedCallback() {
    if (localStorage.getItem(DOPPIO.storageKeys.shippingBannerDismissed) === '1') {
      this.hidden = true;
      return;
    }
    const close = this.querySelector('[data-action="close"]');
    if (close) {
      close.addEventListener('click', () => {
        this.hidden = true;
        localStorage.setItem(DOPPIO.storageKeys.shippingBannerDismissed, '1');
      });
    }
  }
}
customElements.define('doppio-shipping-banner', DoppioShippingBanner);

/* ============================================
   3. Cart-Helpers — Free-Shipping-Progress + Sample-Cross-Sell
   Wir extenden NICHT Impact's CartDrawer (Klassen-Imports sind brittle),
   sondern hängen Logic an die globalen cart:change/cart:refresh Events
   und manipulieren das DOM des Cart-Drawers per CSS-Hook.
   ============================================ */

async function fetchCartJSON() {
  const r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
  return r.json();
}

function renderShippingProgress(cart) {
  const target = document.querySelector('[data-doppio-shipping-progress]');
  if (!target) return;
  const remaining = Math.max(0, DOPPIO.freeShippingThresholdCents - cart.total_price);
  const pct = Math.min(100, (cart.total_price / DOPPIO.freeShippingThresholdCents) * 100);
  const complete = remaining === 0;

  target.classList.toggle('doppio-shipping-progress--complete', complete);
  target.innerHTML = `
    <div class="doppio-shipping-progress__label">
      ${complete
        ? `<span>Versandkostenfrei freigeschaltet</span><span>✓</span>`
        : `<span>Noch ${fmtMoney(remaining)} bis Versandkostenfrei</span><span>${fmtMoney(cart.total_price)} / ${fmtMoney(DOPPIO.freeShippingThresholdCents)}</span>`}
    </div>
    <div class="doppio-shipping-progress__bar">
      <div class="doppio-shipping-progress__fill" style="width: ${pct}%"></div>
    </div>
  `;
}

async function renderSampleCrossSell(cart) {
  const target = document.querySelector('[data-doppio-sample-slot]');
  if (!target) return;

  if (!DOPPIO.sampleEnabled) {        // Gratisprobe global deaktiviert (Cascara nicht verfügbar)
    target.innerHTML = '';
    return;
  }

  const eligible =
    cart.total_price >= DOPPIO.sampleTriggerCents &&
    !cart.items.some((i) => i.handle === DOPPIO.sampleProductHandle);

  if (!eligible) {
    target.innerHTML = '';
    return;
  }

  try {
    const r = await fetch(`/products/${DOPPIO.sampleProductHandle}.js`);
    if (!r.ok) return;
    const product = await r.json();
    const variant = product.variants[0];
    target.innerHTML = `
      <div class="doppio-sample-cross-sell" role="region" aria-label="Cross-Sell">
        ${variant.featured_image
          ? `<img src="${variant.featured_image.src}" alt="${product.title}" class="doppio-sample-cross-sell__image" loading="lazy" width="64" height="64">`
          : `<div class="doppio-sample-cross-sell__image"></div>`}
        <div class="doppio-sample-cross-sell__info">
          <p class="doppio-sample-cross-sell__title">${product.title}</p>
          <p class="doppio-sample-cross-sell__price">+ ${fmtMoney(variant.price)} hinzufügen</p>
        </div>
        <button class="doppio-sample-cross-sell__btn" data-variant-id="${variant.id}" data-action="doppio-add-sample">+</button>
      </div>
    `;
  } catch (err) {
    /* silently fail — Sample-Slot bleibt leer */
  }
}

document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-action="doppio-add-sample"]');
  if (!btn) return;
  e.preventDefault();
  btn.disabled = true;
  btn.textContent = '…';
  try {
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: parseInt(btn.dataset.variantId, 10), quantity: 1 }),
    });
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'));
  } catch (err) {
    btn.disabled = false;
    btn.textContent = '+';
  }
});

// WP-02: Abo-Spar-Anpreisung im Cart-Drawer
// Prüft pro Cart-Item ob ein Einmalkauf vorliegt UND ob das Produkt
// Selling-Plans hätte. Wenn ja: zeigt Hinweis-Banner im Cart-Drawer.
async function renderAboSuggestion(cart) {
  const target = document.querySelector('[data-doppio-abo-suggestion]');
  if (!target) return;

  // Items ohne selling_plan herausfiltern
  const oneTime = (cart.items || []).filter(
    (i) => !i.selling_plan_allocation && i.product_has_only_default_variant !== undefined,
  );
  if (oneTime.length === 0) { target.innerHTML = ''; return; }

  // Pro Item Produkt-Endpoint anfragen ob Selling-Plans existieren
  const candidates = [];
  for (const item of oneTime) {
    try {
      const r = await fetch(`/products/${item.handle}.js`);
      if (!r.ok) continue;
      const p = await r.json();
      if (p.requires_selling_plan === false && Array.isArray(p.selling_plan_groups) && p.selling_plan_groups.length > 0) {
        const plan = p.selling_plan_groups[0]?.selling_plans?.[0];
        if (plan) candidates.push({ item, product: p, plan });
      }
    } catch (_) { /* skip */ }
  }
  if (candidates.length === 0) { target.innerHTML = ''; return; }

  const first = candidates[0];
  const discount = first.plan?.price_adjustments?.[0]?.value;
  const isPct = first.plan?.price_adjustments?.[0]?.value_type === 'percentage';
  const label = (isPct && discount) ? `${discount} %` : 'dauerhaft';

  target.innerHTML = `
    <div class="doppio-cart-abo-hint" role="region" aria-label="Abo-Hinweis">
      <span>Mit Abo sparst du <strong>${label}</strong> pro Lieferung — flexibel pausierbar.</span>
      <a class="doppio-cart-abo-hint__link" href="${first.product.url}#abo">Abo auswählen →</a>
    </div>
  `;
}

// Initial-Render bei Page-Load und re-render bei Cart-Änderungen
async function syncCartUI() {
  try {
    const cart = await fetchCartJSON();
    renderShippingProgress(cart);
    await renderSampleCrossSell(cart);
    await renderAboSuggestion(cart);
  } catch (err) { /* offline o.ae. */ }
}

document.addEventListener('DOMContentLoaded', syncCartUI);
document.addEventListener('cart:change', syncCartUI);
document.addEventListener('cart:refresh', syncCartUI);

/* ============================================
   4. Abo-Toggle — Selling-Plan-Switcher
   Erwartet folgendes Markup in der custom-section:
     <doppio-abo-toggle data-product-id="...">
       <input type="radio" name="selling_plan" value="123456">
       <input type="radio" name="selling_plan" value="">
     </doppio-abo-toggle>
   Der Wert wird über das Impact-product-form per hidden input gesynct.
   ============================================ */
class DoppioAboToggle extends HTMLElement {
  connectedCallback() {
    this.form = this.closest('form[is="product-form"], form[action*="/cart/add"]');
    this.intervalSelect = this.querySelector('[data-doppio-interval]');
    this.addEventListener('change', this.onChange.bind(this));
    // Initialisierungs-Sync
    queueMicrotask(() => this.syncToForm());
  }
  onChange(e) {
    if (e.target.name === 'selling_plan' || e.target === this.intervalSelect) {
      this.syncToForm();
    }
  }
  syncToForm() {
    if (!this.form) return;
    const checked = this.querySelector('input[name="selling_plan"]:checked');
    const value = checked ? checked.value : '';
    let hidden = this.form.querySelector('input[name="selling_plan"]');
    if (!hidden) {
      hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'selling_plan';
      this.form.appendChild(hidden);
    }
    hidden.value = value;
    // Visual-Cue: Interval-Dropdown nur sichtbar wenn Abo aktiv
    if (this.intervalSelect) {
      const aboActive = !!value;
      this.intervalSelect.hidden = !aboActive;
    }
    this.dispatchEvent(new CustomEvent('doppio:abo-toggle', {
      bubbles: true,
      detail: { sellingPlanId: value || null },
    }));
  }
}
customElements.define('doppio-abo-toggle', DoppioAboToggle);

/* ============================================
   5. Grind-Selector — Brüh-Methode synct mit Variant-Option
   Erwartet:
     <doppio-grind-selector data-option-name="Mahlgrad">
       <input type="radio" name="doppio-grind" value="Ganze Bohne">
       ...
     </doppio-grind-selector>
   Synct mit Impact's variant-picker via simuliertem Change-Event auf
   dem entsprechenden Variant-Input.
   ============================================ */
class DoppioGrindSelector extends HTMLElement {
  connectedCallback() {
    this.optionName = this.dataset.optionName || 'Mahlgrad';
    this.addEventListener('change', (e) => {
      if (e.target.name !== 'doppio-grind') return;
      this.propagateToVariantPicker(e.target.value);
    });
  }
  propagateToVariantPicker(value) {
    // Variant-Picker arbeitet mit Inputs `option-{index}` oder einer
    // variant-picker custom-element-Schnittstelle. Wir versuchen beide Wege.
    const form = this.closest('form');
    if (!form) return;

    // Weg 1: option-Inputs (Standard-Shopify-Markup)
    const optionInputs = form.querySelectorAll('input[name^="option"]');
    optionInputs.forEach((input) => {
      const label = input.closest('label')?.textContent?.trim();
      if (label && label.toLowerCase().includes(value.toLowerCase())) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    // Weg 2: variant-picker exposed API (Impact)
    const variantPicker = form.querySelector('variant-picker');
    if (variantPicker && typeof variantPicker.setOption === 'function') {
      variantPicker.setOption(this.optionName, value);
    }
  }
}
customElements.define('doppio-grind-selector', DoppioGrindSelector);

/* ============================================
   6. Auto-Stagger — Headlines mit data-stagger werden in
   word-by-word reveal-spans aufgebrochen, lazy beim Sichtbarwerden.
   ============================================ */
(() => {
  const splitToWords = (el) => {
    if (el.dataset.staggered === '1') return;
    el.dataset.staggered = '1';
    const text = el.textContent.trim();
    const words = text.split(/\s+/);
    const frag = document.createDocumentFragment();
    words.forEach((w, i) => {
      const span = document.createElement('span');
      span.textContent = w + (i < words.length - 1 ? ' ' : '');
      span.style.setProperty('--i', i);
      frag.appendChild(span);
    });
    el.classList.add('doppio-stagger');
    el.textContent = '';
    el.appendChild(frag);
  };

  const initStagger = () => {
    document.querySelectorAll('[data-stagger]:not([data-staggered])').forEach((el) => {
      splitToWords(el);
      // Reveal-Container schauen ob schon visible
      const reveal = el.closest('.doppio-reveal');
      if (!reveal || reveal.classList.contains('is-visible')) {
        requestAnimationFrame(() => el.classList.add('is-revealed'));
      }
    });
  };

  initStagger();
  document.addEventListener('shopify:section:load', initStagger);
})();

/* ============================================
   7. Number-Counter — Trust-Manifest-Strip
   Verwendung: <span data-count-to="2027" data-count-suffix="">…</span>
   ============================================ */
(() => {
  if (typeof IntersectionObserver === 'undefined') return;
  const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));
  const animateCount = (el) => {
    const target = parseFloat(el.dataset.countTo);
    const decimals = parseInt(el.dataset.countDecimals || '0', 10);
    const suffix = el.dataset.countSuffix || '';
    const prefix = el.dataset.countPrefix || '';
    const duration = parseInt(el.dataset.countDuration || '1400', 10);
    if (!Number.isFinite(target)) return;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const v = target * easeOutExpo(t);
      el.textContent = prefix + v.toFixed(decimals).replace('.', ',') + suffix;
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };
  const obs = new IntersectionObserver(
    (entries) => entries.forEach((e) => {
      if (e.isIntersecting) { animateCount(e.target); obs.unobserve(e.target); }
    }),
    { threshold: 0.4 }
  );
  const init = () => document.querySelectorAll('[data-count-to]:not([data-counted])').forEach((el) => {
    el.dataset.counted = '1';
    obs.observe(el);
  });
  init();
  document.addEventListener('shopify:section:load', init);
})();

/* ============================================
   8. Magnetic Buttons — subtle cursor pull
   Verwendung: <a data-magnetic>...</a>
   - Nur über pointer:hover Devices (kein Touch)
   - Respektiert prefers-reduced-motion
   - max 8px Translate, 0.25 multiplier
   ============================================ */
(() => {
  const canHover = matchMedia('(hover: hover) and (pointer: fine)').matches;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!canHover || reduced) return;

  const STRENGTH = 0.25;
  const init = (el) => {
    if (el.dataset.magneticInit === '1') return;
    el.dataset.magneticInit = '1';
    let raf = null;
    const onMove = (e) => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const r = el.getBoundingClientRect();
        const x = (e.clientX - r.left - r.width / 2) * STRENGTH;
        const y = (e.clientY - r.top - r.height / 2) * STRENGTH;
        el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      });
    };
    const onLeave = () => {
      if (raf) cancelAnimationFrame(raf);
      el.style.transform = '';
    };
    el.addEventListener('pointermove', onMove, { passive: true });
    el.addEventListener('pointerleave', onLeave);
  };
  const initAll = () => document.querySelectorAll('[data-magnetic]:not([data-magnetic-init])').forEach(init);
  initAll();
  document.addEventListener('shopify:section:load', initAll);
})();

/* ============================================
   9. Sticky Bottom Buy-Bar Mobile — fades in nach Hero-Scroll
   Aktiviert sich nur auf product-Pages, < 750px viewport.
   Erkennt das Haupt-Add-to-Cart-Button via [data-doppio-main-cta] oder
   alternativ Impact's [is="confirm-button"][type="submit"] im main-product.
   ============================================ */
(() => {
  if (typeof IntersectionObserver === 'undefined') return;
  if (!document.body.classList.contains('template-product')) return;
  if (window.innerWidth >= 750) return;

  // Wait for DOM to be ready
  const init = () => {
    const mainBtn = document.querySelector('main [is="confirm-button"][type="submit"], main .product-form__submit, [data-doppio-main-cta]');
    if (!mainBtn) return;

    // Build the bar
    const bar = document.createElement('div');
    bar.className = 'doppio-mobile-buy-bar';
    bar.setAttribute('aria-hidden', 'true');
    bar.style.transform = 'translateY(110%)';
    bar.innerHTML = `
      <button type="button" class="doppio-mobile-buy-bar__btn" aria-label="In den Warenkorb">
        <span>In den Warenkorb</span>
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
      </button>
    `;
    document.body.appendChild(bar);

    // Click delegates to main button
    bar.querySelector('button').addEventListener('click', () => mainBtn.click());

    // Toggle visibility when main button is out of view
    const obs = new IntersectionObserver(
      ([entry]) => {
        const visible = !entry.isIntersecting;
        bar.style.transform = visible ? 'translateY(0)' : 'translateY(110%)';
        bar.setAttribute('aria-hidden', String(!visible));
      },
      { threshold: 0, rootMargin: '0px 0px -10% 0px' }
    );
    obs.observe(mainBtn);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================
   10. Buy-CTAs — "Jetzt kaufen" (now) + "In den Warenkorb" (cart)
   + Variant-Drawer für Multi-Variant-Produkte
   ============================================ */
(() => {
  const TEMPLATE_ID = 'doppio-quick-buy-drawer-template';
  let drawer = null;

  const addToCart = async (variantId, quantity = 1) => {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity }),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  };

  const openCartDrawer = () => {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.show === 'function') {
      setTimeout(() => cartDrawer.show(), 350);
    }
  };

  const setLoading = (btn, loading) => {
    if (loading) {
      btn.dataset.state = 'loading';
      btn.dataset.originalText = btn.querySelector('span')?.textContent || btn.textContent;
      const lbl = btn.querySelector('span') || btn;
      lbl.textContent = 'Einen Moment …';
    } else {
      delete btn.dataset.state;
      const lbl = btn.querySelector('span') || btn;
      if (btn.dataset.originalText) lbl.textContent = btn.dataset.originalText;
    }
  };

  const handleDirectAction = async (btn, action) => {
    if (btn.dataset.state) return;
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    setLoading(btn, true);
    try {
      await addToCart(variantId);
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'));
      if (action === 'now') {
        window.location.href = '/checkout';
      } else {
        // success state on button
        btn.dataset.state = 'success';
        const lbl = btn.querySelector('span') || btn;
        lbl.textContent = 'Im Warenkorb ✓';
        openCartDrawer();
        setTimeout(() => setLoading(btn, false), 1400);
      }
    } catch (err) {
      console.warn('[Doppio Buy]', err);
      setLoading(btn, false);
      alert('Hoppla — bitte erneut versuchen.');
    }
  };

  // Direct buttons (single-variant)
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('button[data-doppio-buy]');
    if (!btn) return;
    const action = btn.dataset.doppioBuy;
    if (action === 'now' || action === 'cart') {
      e.preventDefault();
      handleDirectAction(btn, action);
    }
  });

  // === Variant-Drawer für Multi-Variant-Produkte ===
  const ensureDrawer = () => {
    if (drawer) return drawer;
    const tpl = document.getElementById(TEMPLATE_ID);
    if (!tpl) return null;
    const frag = tpl.content.cloneNode(true);
    drawer = frag.firstElementChild;
    document.body.appendChild(drawer);

    // Close handlers
    drawer.addEventListener('click', (e) => {
      if (e.target.closest('[data-doppio-qbd-close]')) closeDrawer();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !drawer.hidden) closeDrawer();
    });
    return drawer;
  };

  const openDrawer = () => {
    const d = ensureDrawer();
    if (!d) return;
    d.hidden = false;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => d.dataset.open = 'true');
  };

  const closeDrawer = () => {
    if (!drawer) return;
    drawer.dataset.open = 'false';
    document.body.style.overflow = '';
    setTimeout(() => { drawer.hidden = true; }, 300);
  };

  const renderDrawer = (product, preferredAction) => {
    const content = drawer.querySelector('[data-doppio-qbd-content]');
    const currentVariant = product.variants.find(v => v.available) || product.variants[0];
    const priceFmt = (cents) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);

    const optionsHtml = product.options.map((optName, optIdx) => {
      const values = [...new Set(product.variants.map(v => v.options[optIdx]))];
      return `
        <div class="doppio-qbd__option" data-option-index="${optIdx}">
          <span class="doppio-qbd__option-label">${optName}</span>
          <div class="doppio-qbd__option-values">
            ${values.map(val => {
              const variantForVal = product.variants.find(v => v.options[optIdx] === val);
              const selected = currentVariant.options[optIdx] === val;
              return `<button type="button" class="doppio-qbd__option-btn"
                              data-value="${val.replace(/"/g, '&quot;')}"
                              aria-pressed="${selected}"
                              ${!variantForVal?.available ? 'aria-disabled="true"' : ''}>${val}</button>`;
            }).join('')}
          </div>
        </div>`;
    }).join('');

    content.innerHTML = `
      <div class="doppio-qbd__product">
        ${product.featured_image ? `<img src="${product.featured_image}" alt="" loading="lazy">` : '<div></div>'}
        <div>
          <h3 class="doppio-qbd__product-title">${product.title}</h3>
          <div class="doppio-qbd__product-price" data-doppio-qbd-price>${priceFmt(currentVariant.price)}</div>
        </div>
      </div>
      <div class="doppio-qbd__options" data-doppio-qbd-options>${optionsHtml}</div>
      <div class="doppio-qbd__actions">
        <button type="button" class="doppio-cta doppio-cta--primary doppio-qbd__primary" data-doppio-qbd-submit data-action="now" data-variant-id="${currentVariant.id}">
          <span>Jetzt kaufen · ${priceFmt(currentVariant.price)}</span>
        </button>
        <button type="button" class="doppio-cta doppio-cta--secondary doppio-qbd__secondary" data-doppio-qbd-submit data-action="cart" data-variant-id="${currentVariant.id}">
          In den Warenkorb
        </button>
      </div>
    `;

    const selectedOptions = [...currentVariant.options];
    const optionsContainer = content.querySelector('[data-doppio-qbd-options]');
    const primaryBtn = content.querySelector('.doppio-qbd__primary');
    const secondaryBtn = content.querySelector('.doppio-qbd__secondary');
    const priceEl = content.querySelector('[data-doppio-qbd-price]');

    const findVariant = () => {
      return product.variants.find(v => v.options.every((opt, i) => opt === selectedOptions[i]));
    };

    const updateSelection = () => {
      const variant = findVariant();
      if (variant) {
        primaryBtn.dataset.variantId = variant.id;
        secondaryBtn.dataset.variantId = variant.id;
        primaryBtn.disabled = !variant.available;
        secondaryBtn.disabled = !variant.available;
        primaryBtn.querySelector('span').textContent = variant.available
          ? `Jetzt kaufen · ${priceFmt(variant.price)}`
          : 'Ausverkauft';
        if (priceEl) priceEl.textContent = priceFmt(variant.price);
      }
    };

    optionsContainer.addEventListener('click', (e) => {
      const btn = e.target.closest('.doppio-qbd__option-btn');
      if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
      const optWrapper = btn.closest('.doppio-qbd__option');
      const optIdx = parseInt(optWrapper.dataset.optionIndex, 10);
      selectedOptions[optIdx] = btn.dataset.value;
      optWrapper.querySelectorAll('.doppio-qbd__option-btn').forEach(b => {
        b.setAttribute('aria-pressed', b.dataset.value === btn.dataset.value ? 'true' : 'false');
      });
      updateSelection();
    });

    const submitAction = async (action, btn) => {
      const variantId = btn.dataset.variantId;
      if (!variantId) return;
      const lbl = btn.querySelector('span') || btn;
      const orig = lbl.textContent;
      btn.disabled = true;
      lbl.textContent = action === 'now' ? 'Weiter zur Kasse …' : 'Wird hinzugefügt …';
      try {
        await addToCart(variantId);
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'));
        if (action === 'now') {
          window.location.href = '/checkout';
          return;
        }
        content.innerHTML = `
          <div class="doppio-qbd__success">
            <div class="doppio-qbd__success-icon">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
            </div>
            <h3 class="doppio-qbd__product-title">Im Warenkorb!</h3>
            <p style="margin: 0.5rem 0 0; color: var(--doppio-navy-60);">${product.title}</p>
          </div>`;
        setTimeout(() => { closeDrawer(); openCartDrawer(); }, 900);
      } catch (err) {
        console.warn('[Doppio Drawer]', err);
        btn.disabled = false;
        lbl.textContent = orig;
        alert('Hoppla — bitte erneut versuchen.');
      }
    };

    primaryBtn.addEventListener('click', () => submitAction('now', primaryBtn));
    secondaryBtn.addEventListener('click', () => submitAction('cart', secondaryBtn));

    // Wenn der User den sekundären CTA der Card geklickt hat, sub-prefer
    if (preferredAction === 'cart') {
      secondaryBtn.focus();
    } else {
      primaryBtn.focus();
    }
  };

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('button[data-doppio-buy^="drawer-"]');
    if (!btn) return;
    e.preventDefault();
    const handle = btn.dataset.productHandle;
    if (!handle) return;
    const action = btn.dataset.doppioBuy === 'drawer-cart' ? 'cart' : 'now';
    ensureDrawer();
    openDrawer();
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) throw new Error('product fetch failed');
      const product = await res.json();
      renderDrawer(product, action);
    } catch (err) {
      console.warn('[Doppio Drawer]', err);
      drawer.querySelector('[data-doppio-qbd-content]').innerHTML = '<div class="doppio-qbd__loading">Konnte das Produkt nicht laden. Bitte erneut versuchen.</div>';
    }
  });
})();

/* ============================================
   11. Transparent Header (nur Homepage, oben)
   Sobald scrollY > 60 -> body.is-scrolled = true
   ============================================ */
(() => {
  if (!document.body.classList.contains('template-index')) return;
  document.documentElement.classList.add('doppio-th');
  const update = () => {
    document.documentElement.classList.toggle('is-scrolled', window.scrollY > 60);
  };
  update();
  let ticking = false;
  window.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => { update(); ticking = false; });
  }, { passive: true });
})();

/* ============================================
   Debug-Helper (nur bei explizitem URL-Param ?doppio-debug=1)
   ============================================ */
if (new URLSearchParams(location.search).has('doppio-debug')) {
  window.DOPPIO = { config: DOPPIO, fmtMoney, fetchCartJSON, syncCartUI };
  console.log('[Doppio] Debug-Mode aktiv. window.DOPPIO verfügbar.');
}

/* ============================================
   WP-01 — Doppio Shop-Wizard (Web-Component)
   Horizontaler Step-Wizard, opens via configured trigger-selector.
   ============================================ */
(() => {
  if (customElements.get('doppio-shop-wizard')) return;

  class DoppioShopWizard extends HTMLElement {
    constructor() {
      super();
      this.state = {
        step: 1,
        sort: null,
        product: null,
        variant: null,
        sellingPlanId: null,
        quantity: 1,
      };
      this._handlers = [];
    }

    connectedCallback() {
      this._cacheRefs();
      this._wireTriggers();
      this._wireUI();
      // ESC to close
      document.addEventListener('keydown', this._onKey = (e) => {
        if (e.key === 'Escape' && !this.hidden) this.close();
      });
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKey);
      this._handlers.forEach((off) => off());
    }

    _cacheRefs() {
      this.refs = {
        panel:        this.querySelector('.dsw__panel'),
        steps:        this.querySelectorAll('.dsw__step'),
        backBtn:      this.querySelector('[data-dsw-back]'),
        closeBtns:    this.querySelectorAll('[data-dsw-close]'),
        panes:        Array.from(this.querySelectorAll('.dsw__pane')),
        productList:  this.querySelector('[data-dsw-product-list]'),
        step2Hint:    this.querySelector('[data-dsw-step2-hint]'),
        variantHost:  this.querySelector('[data-dsw-variant-host]'),
        summaryHost:  this.querySelector('[data-dsw-summary-host]'),
        footer:       this.querySelector('[data-dsw-footer]'),
        footerMeta:   this.querySelector('[data-dsw-footer-meta]'),
      };
    }

    _wireTriggers() {
      const selector = this.dataset.triggerSelector;
      if (!selector) return;
      const links = document.querySelectorAll(selector);
      links.forEach((link) => {
        const handler = (e) => {
          e.preventDefault();
          this.open();
        };
        link.addEventListener('click', handler);
        this._handlers.push(() => link.removeEventListener('click', handler));
      });
    }

    _wireUI() {
      this.refs.closeBtns.forEach((b) => b.addEventListener('click', () => this.close()));
      this.refs.backBtn.addEventListener('click', () => this._goBack());

      // Step 1: Sort-Tile click
      this.querySelectorAll('[data-dsw-pick-sort]').forEach((btn) => {
        btn.addEventListener('click', () => this._pickSort(btn));
      });

      // Footer-Actions (Step 4)
      this.refs.footer.querySelectorAll('[data-dsw-action]').forEach((btn) => {
        btn.addEventListener('click', () => this._confirm(btn.dataset.dswAction));
      });
    }

    open() {
      this.hidden = false;
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => this.classList.add('is-open'));
      this._goTo(1);
    }

    close() {
      this.classList.remove('is-open');
      setTimeout(() => {
        this.hidden = true;
        document.body.style.overflow = '';
        this._reset();
      }, 250);
    }

    _reset() {
      this.state = { step: 1, sort: null, product: null, variant: null, sellingPlanId: null, quantity: 1 };
      this._goTo(1);
      // Clear dynamic panes
      this.refs.productList.innerHTML = '<li class="dsw__loading">Lade Produkte …</li>';
      this.refs.variantHost.innerHTML = '<div class="dsw__loading">Lade Optionen …</div>';
      this.refs.summaryHost.innerHTML = '<div class="dsw__loading">Vorbereiten …</div>';
    }

    _goTo(step) {
      this.state.step = step;
      this.refs.steps.forEach((el) => {
        const s = parseInt(el.dataset.step, 10);
        el.classList.toggle('is-active', s === step);
        el.classList.toggle('is-done', s < step);
      });
      this.refs.panes.forEach((pane) => {
        const s = parseInt(pane.dataset.pane, 10);
        pane.hidden = s !== step;
        pane.classList.toggle('is-active', s === step);
      });
      this.refs.backBtn.hidden = step === 1;
      this.refs.footer.hidden = step !== 4;
    }

    _goBack() {
      if (this.state.step <= 1) return;
      this._goTo(this.state.step - 1);
    }

    async _pickSort(btn) {
      const handle = btn.dataset.collectionHandle;
      const label = btn.querySelector('.dsw__tile-label')?.textContent?.trim() || handle;
      this.state.sort = { handle, label };
      this._goTo(2);
      this.refs.step2Hint.textContent = `Welches Produkt aus „${label}"?`;
      await this._loadProducts(handle);
    }

    async _loadProducts(handle) {
      this.refs.productList.innerHTML = '<li class="dsw__loading">Lade Produkte …</li>';
      try {
        const r = await fetch(`/collections/${handle}/products.json?limit=20`);
        if (!r.ok) throw new Error('collection fetch failed');
        const data = await r.json();
        const products = (data.products || []).filter((p) => p.available !== false);
        if (products.length === 0) {
          this.refs.productList.innerHTML = '<li class="dsw__empty">Aktuell keine verfügbaren Produkte.</li>';
          return;
        }
        this.refs.productList.innerHTML = products.map((p) => {
          const img = p.images?.[0] ? `https:${p.images[0]}` : '';
          const minPrice = p.variants?.[0]?.price || '';
          return `
            <li>
              <button type="button" class="dsw__tile dsw__tile--product" data-dsw-pick-product data-handle="${p.handle}">
                ${img ? `<span class="dsw__tile-img"><img src="${img}" alt="" loading="lazy" decoding="async"></span>` : '<span class="dsw__tile-img dsw__tile-img--solid"></span>'}
                <span class="dsw__tile-body">
                  <span class="dsw__tile-label">${p.title}</span>
                  <span class="dsw__tile-sub">${minPrice ? 'ab ' + minPrice + ' €' : ''}</span>
                </span>
              </button>
            </li>`;
        }).join('');
        this.refs.productList.querySelectorAll('[data-dsw-pick-product]').forEach((btn) => {
          btn.addEventListener('click', () => this._pickProduct(btn.dataset.handle));
        });
      } catch (err) {
        console.warn('[dsw] loadProducts', err);
        this.refs.productList.innerHTML = '<li class="dsw__empty">Produkte konnten nicht geladen werden.</li>';
      }
    }

    async _pickProduct(handle) {
      this._goTo(3);
      this.refs.variantHost.innerHTML = '<div class="dsw__loading">Lade Optionen …</div>';
      try {
        const r = await fetch(`/products/${handle}.js`);
        if (!r.ok) throw new Error('product fetch failed');
        const product = await r.json();
        this.state.product = product;
        // Default variant = first available
        this.state.variant = (product.variants || []).find((v) => v.available) || product.variants?.[0] || null;
        this.state.sellingPlanId = null;
        // Default to Abo (Variante A des Plans, Konsistenz mit Bestseller-Logik)
        const group = product.selling_plan_groups?.[0];
        const plan = group?.selling_plans?.[0];
        if (plan) this.state.sellingPlanId = plan.id;
        this._renderVariantStep(product);
      } catch (err) {
        console.warn('[dsw] pickProduct', err);
        this.refs.variantHost.innerHTML = '<div class="dsw__empty">Produkt konnte nicht geladen werden.</div>';
      }
    }

    _renderVariantStep(product) {
      const fmt = (cents) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
      const variant = this.state.variant;

      // Option-Picker
      const optionsHtml = (product.options || []).map((opt, idx) => {
        const values = [...new Set((product.variants || []).map((v) => v.options[idx]))];
        return `
          <fieldset class="dsw__opt" data-opt-index="${idx}">
            <legend class="dsw__opt-legend">${opt.name || opt}</legend>
            <div class="dsw__opt-values">
              ${values.map((val) => {
                const variantForVal = product.variants.find((v) => v.options[idx] === val);
                const selected = variant?.options[idx] === val;
                const disabled = !variantForVal?.available;
                return `<button type="button" class="dsw__opt-btn" data-value="${val.replace(/"/g, '&quot;')}" aria-pressed="${selected}" ${disabled ? 'aria-disabled="true"' : ''}>${val}</button>`;
              }).join('')}
            </div>
          </fieldset>`;
      }).join('');

      // Liefer-Modus (Abo / Einmalig)
      const groups = product.selling_plan_groups || [];
      const plan = groups[0]?.selling_plans?.[0];
      const priceAdjVal = plan?.price_adjustments?.[0]?.value;
      const priceAdjType = plan?.price_adjustments?.[0]?.value_type;
      const discountLabel = (priceAdjType === 'percentage' && priceAdjVal) ? `${priceAdjVal} %` : null;

      const deliveryHtml = plan ? `
        <fieldset class="dsw__delivery">
          <legend class="dsw__opt-legend">Wie möchtest du beliefert werden?</legend>
          <label class="dsw__delivery-option">
            <input type="radio" name="dsw-delivery" value="abo" ${this.state.sellingPlanId ? 'checked' : ''}>
            <span>
              <strong>Im Abo${discountLabel ? ` · ${discountLabel} sparen` : ''}</strong>
              <small>flexibel pausierbar, jederzeit kündbar</small>
            </span>
          </label>
          <label class="dsw__delivery-option">
            <input type="radio" name="dsw-delivery" value="once" ${!this.state.sellingPlanId ? 'checked' : ''}>
            <span><strong>Einmaliger Kauf</strong></span>
          </label>
        </fieldset>` : '';

      this.refs.variantHost.innerHTML = `
        <header class="dsw__variant-header">
          ${product.featured_image ? `<img src="${product.featured_image}" alt="" class="dsw__variant-img" loading="lazy">` : ''}
          <div>
            <h3>${product.title}</h3>
            <div class="dsw__variant-price" data-dsw-price>${fmt(variant.price)}</div>
          </div>
        </header>
        <div class="dsw__variant-options">
          ${optionsHtml}
          ${deliveryHtml}
        </div>
        <div class="dsw__variant-actions">
          <button type="button" class="doppio-cta doppio-cta--primary" data-dsw-next>
            <span>Weiter zur Übersicht</span>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
          </button>
        </div>
      `;

      // Wire option-click
      this.refs.variantHost.querySelectorAll('.dsw__opt-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (btn.getAttribute('aria-disabled') === 'true') return;
          const fieldset = btn.closest('.dsw__opt');
          const idx = parseInt(fieldset.dataset.optIndex, 10);
          fieldset.querySelectorAll('.dsw__opt-btn').forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
          const selected = btn.dataset.value;
          // resolve variant
          const newOpts = [...(this.state.variant?.options || [])];
          newOpts[idx] = selected;
          const found = (product.variants || []).find((v) => v.options.every((o, i) => o === newOpts[i]));
          if (found) {
            this.state.variant = found;
            const priceEl = this.refs.variantHost.querySelector('[data-dsw-price]');
            if (priceEl) priceEl.textContent = fmt(found.price);
          }
        });
      });

      // Wire delivery-radio
      this.refs.variantHost.querySelectorAll('input[name="dsw-delivery"]').forEach((r) => {
        r.addEventListener('change', () => {
          if (r.value === 'abo') {
            this.state.sellingPlanId = plan?.id || null;
          } else {
            this.state.sellingPlanId = null;
          }
        });
      });

      // Wire next
      this.refs.variantHost.querySelector('[data-dsw-next]')?.addEventListener('click', () => this._toSummary());
    }

    _toSummary() {
      this._goTo(4);
      const product = this.state.product;
      const variant = this.state.variant;
      const fmt = (cents) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
      const aboLabel = this.state.sellingPlanId ? 'Im Abo' : 'Einmaliger Kauf';

      this.refs.summaryHost.innerHTML = `
        <div class="dsw__summary-card">
          ${product.featured_image ? `<img src="${product.featured_image}" alt="" class="dsw__summary-img" loading="lazy">` : ''}
          <div class="dsw__summary-body">
            <h3>${product.title}</h3>
            <p class="dsw__summary-meta">
              ${variant.title && variant.title !== 'Default Title' ? `<span>${variant.title}</span>` : ''}
              <span>${aboLabel}</span>
            </p>
            <p class="dsw__summary-price">${fmt(variant.price)}</p>
          </div>
        </div>
        <p class="dsw__summary-hint">Kostenloser Versand ab 50 € · Lieferung in 1–3 Tagen.</p>
      `;
      this.refs.footerMeta.textContent = `${product.title} · ${fmt(variant.price)}`;
    }

    async _confirm(action) {
      if (!this.state.variant) return;
      const btn = this.refs.footer.querySelector(`[data-dsw-action="${action}"]`);
      const orig = btn?.querySelector('span')?.textContent || btn?.textContent;
      if (btn) { btn.disabled = true; (btn.querySelector('span') || btn).textContent = action === 'now' ? 'Weiter zur Kasse …' : 'Wird hinzugefügt …'; }
      try {
        const body = { id: this.state.variant.id, quantity: this.state.quantity };
        if (this.state.sellingPlanId) body.selling_plan = this.state.sellingPlanId;
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!r.ok) throw new Error(await r.text());
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'));
        if (action === 'now') {
          window.location.href = '/checkout';
          return;
        }
        // close + open cart-drawer (Impact)
        this.close();
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.show === 'function') setTimeout(() => cartDrawer.show(), 350);
      } catch (err) {
        console.warn('[dsw] confirm', err);
        if (btn) { btn.disabled = false; (btn.querySelector('span') || btn).textContent = orig; }
        alert('Hoppla — bitte erneut versuchen.');
      }
    }
  }

  customElements.define('doppio-shop-wizard', DoppioShopWizard);
})();

/* ============================================
   Doppio Shop-Mega-Dropdown
   ============================================
   Pure Web Component, kein Dependency auf Shopify-Linklist.
   Wird automatisch eingehaengt unter jedem Header-Link der auf
   /collections/all, /collections/alle-produkte zeigt ODER
   text-Inhalt "Shop" hat.

   Layout: 7 Category-Tiles + Featured Cards (Abo, Bestseller).
   Tiles linken auf /search?q=<term> (immer 200 OK auf jeder Shopify-Site,
   unabhängig von Collection-Existenz).

   Animation: Slide-down von Header-Höhe, pure CSS-Transition.
*/
(() => {
  const SHOP_LABEL = /^\s*shop\s*$/i;
  const SHOP_HREF_RE = /\/collections\/(all|alle-produkte)(\b|\/|$|\?)/i;

  // Kategorien — Reihenfolge = Umsatz-Top + strategische Hervorhebung.
  // collection: optionaler Shopify-Collection-Handle. Gesetzt -> kuratierte Collection,
  // leer -> Live-Suche (Predictive Search) als Fallback. Spaeter ohne Code-Aenderung nachruestbar.
  const CATEGORIES = [
    { key: 'crema',         label: 'Crema',         caption: 'Hausmischung · Daily-Driver', pack: 'crema',       q: 'Crema',          collection: '' },
    { key: 'espresso',      label: 'Espresso',      caption: 'Italienische Trommelröstung', pack: 'espresso',    q: 'Espresso',       collection: '' },
    { key: 'filterkaffee',  label: 'Filterkaffee',  caption: 'V60 · Aeropress · Chemex',    pack: 'filterkaffee',q: 'Filterkaffee',   collection: '' },
    { key: 'entkoffeiniert',label: 'Entkoffeiniert',caption: 'Vollmundig ohne Koffein',     pack: 'navy',        q: 'Entkoffeiniert', collection: '' },
    { key: 'coffeebags',    label: 'Coffeebags',    caption: 'Easy unterwegs',              pack: 'cream',       q: 'Coffeebags',     collection: '' },
    { key: 'cascara',       label: 'Cascara',       caption: 'Kaffeekirschen-Tee',          pack: 'gold',        q: 'Cascara',        collection: '' },
  ];

  const FEATURED = [
    { label: 'Kaffee-Abo', caption: 'Frisch geröstet · jederzeit kündbar',
      href: '/pages/kaffee-abo', cta: 'Im Abo sparen', tone: 'gold' },
    { label: 'Bestseller', caption: 'Top-4 Sorten · 87 % Umsatz',
      href: '/collections/bestseller', cta: 'Zu den Bestsellern', tone: 'navy' },
    { label: 'Alle Produkte', caption: 'Komplettes Sortiment ansehen',
      href: '/collections/all', cta: 'Alle ansehen →', tone: 'paper' },
  ];

  class DoppioShopMega extends HTMLElement {
    constructor() {
      super();
      this._open = false;
      this._anchorLinks = [];
      this._cache = {};
      this._activeKey = null;
      this._abort = null;
      this._catEls = [];
      this._desktopHost = null;
      this._mq = (window.matchMedia ? window.matchMedia('(min-width: 900px)') : { matches: true });
      this._onDocClick = this._onDocClick.bind(this);
      this._onKey = this._onKey.bind(this);
      this._onScroll = this._onScroll.bind(this);
    }
    connectedCallback() {
      if (this._mounted) return;
      this._mounted = true;
      this.innerHTML = this._template();
      this.classList.add('dsm');
      this.setAttribute('hidden', '');
      this.setAttribute('role', 'dialog');
      this.setAttribute('aria-modal', 'false');
      this.setAttribute('aria-label', 'Shop-Übersicht');

      // Schließen via Backdrop
      this.querySelector('.dsm__backdrop')?.addEventListener('click', () => this.close());
      this.querySelector('[data-dsm-close]')?.addEventListener('click', () => this.close());

      this._wireUpTriggers();
      this._wireCats();
      // Observer für nachgeladene Header-Items (Mega-Menus etc.)
      const obs = new MutationObserver(() => this._wireUpTriggers());
      obs.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => obs.disconnect(), 4000);
    }
    _template() {
      const cats = CATEGORIES.map((c, i) => `
        <a class="dsm__tile" data-pack-code="${c.pack}"
           href="${this._catUrl(c)}"
           data-dsm-cat="${i}" data-dsm-item>
          <span class="dsm__tile-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 8h13a4 4 0 1 1 0 8h-1"/>
              <path d="M4 8v6a4 4 0 0 0 4 4h5a4 4 0 0 0 4-4V8H4z"/>
              <path d="M7 4c0 1.5 1 2 1 3M11 4c0 1.5 1 2 1 3M15 4c0 1.5 1 2 1 3"/>
            </svg>
          </span>
          <span class="dsm__tile-body">
            <span class="dsm__tile-label">${c.label}</span>
            <span class="dsm__tile-caption">${c.caption}</span>
          </span>
          <span class="dsm__tile-arrow" aria-hidden="true">→</span>
        </a>`).join('');

      const feats = FEATURED.map(f => `
        <a class="dsm__feature" data-tone="${f.tone}" href="${f.href}" data-dsm-item>
          <span class="dsm__feature-eyebrow">Empfehlung</span>
          <span class="dsm__feature-label">${f.label}</span>
          <span class="dsm__feature-caption">${f.caption}</span>
          <span class="dsm__feature-cta">${f.cta}</span>
        </a>`).join('');

      return `
        <div class="dsm__backdrop" aria-hidden="true"></div>
        <div class="dsm__panel" role="document">
          <header class="dsm__head">
            <span class="dsm__kicker">Unser Sortiment.</span>
            <h2 class="dsm__title">Was darf es heute sein?</h2>
            <button type="button" class="dsm__close" data-dsm-close aria-label="Schließen">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
            </button>
          </header>
          <div class="dsm__body">
            <div class="dsm__cats-col">
              <span class="dsm__col-title">Kaffeesorten</span>
              <div class="dsm__cats">${cats}</div>
            </div>
            <div class="dsm__detail" aria-live="polite">
              <div class="dsm__detail-grid" data-dsm-grid></div>
              <a class="dsm__detail-all" href="#" data-dsm-all hidden></a>
            </div>
          </div>
          <div class="dsm__features-wrap">
            <span class="dsm__col-title">Direkt loslegen</span>
            <div class="dsm__features">${feats}</div>
          </div>
        </div>`;
    }
    _wireUpTriggers() {
      // "Shop" ist auf Desktop ein <summary>, im Mobile-Drawer ein <button> -> alle abdecken.
      const candidates = Array.from(document.querySelectorAll(
        'header summary, header a, header button, [class*="header"] summary, [class*="header"] a, [class*="header"] button, .mega-menu a'
      ));
      candidates.forEach((el) => {
        if (el.__dsmWired) return;
        const text = (el.textContent || '').trim();
        const href = el.getAttribute('href') || el.getAttribute('data-url') || '';
        const isShop = SHOP_LABEL.test(text) || SHOP_HREF_RE.test(href);
        if (!isShop) return;
        el.__dsmWired = true;

        // Natives Impact-Mega-Menue dieses Items auf Desktop unterdruecken (CSS-Klasse)
        const details = el.closest('details');
        if (details) details.classList.add('dsm-suppressed');

        // Klick/Tap (Capture) -> eigenes Menue statt nativem Toggle (Desktop + Mobile)
        el.addEventListener('click', (e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          e.stopPropagation();
          if (!this._mq.matches) this._closeNativeDrawer(el);
          this.toggle(el);
        }, true);

        // Hover/Fokus -> oeffnen (nur Desktop)
        const openOnHover = () => { if (this._mq.matches && !this._open) this.open(el); };
        el.addEventListener('mouseenter', openOnHover);
        el.addEventListener('focus', openOnHover, true);
      });
      this._anchorLinks = candidates;
    }
    _closeNativeDrawer(el) {
      // Beim Mobile-Tap den offenen Impact-Drawer schliessen, damit unser Sheet frei liegt.
      try {
        const drawer = el.closest('navigation-drawer, dialog, [role="dialog"], .drawer');
        if (!drawer) return;
        if (typeof drawer.hide === 'function') { drawer.hide(); return; }
        if (typeof drawer.close === 'function') { drawer.close(); return; }
        const closeBtn = drawer.querySelector('[is="close-button"], .close-button, button[aria-label*="lose"], button[aria-label*="chlie"]');
        if (closeBtn) closeBtn.click();
      } catch (e) { /* Drawer bleibt sonst unter dem Sheet, unkritisch */ }
    }
    toggle(anchor) { this._open ? this.close() : this.open(anchor); }
    // Desktop: in die sticky Header-Section einhaengen (absolute top:100% -> buendig, keine Luecke).
    // Mobile: als Fullscreen-Sheet an den body (kein transform-Containing-Block durch hide-on-scroll).
    _mountForViewport(anchor) {
      if (this._mq.matches) {
        const host = (anchor && anchor.closest('.shopify-section')) || this._desktopHost
          || (document.querySelector('store-header') && document.querySelector('store-header').closest('.shopify-section'))
          || document.querySelector('.shopify-section-header, .section-header');
        this.classList.remove('dsm--mobile');
        this.classList.add('dsm--docked');
        if (host && this.parentElement !== host) host.appendChild(this);
        if (host) this._desktopHost = host;
      } else {
        this.classList.remove('dsm--docked');
        this.classList.add('dsm--mobile');
        if (this.parentElement !== document.body) document.body.appendChild(this);
      }
    }
    open(anchor) {
      this._mountForViewport(anchor);
      this.removeAttribute('hidden');
      // microtask so transition picks up
      requestAnimationFrame(() => this.classList.add('is-open'));
      this._open = true;
      // Erste Kategorie automatisch aktiv + Produkte laden (Desktop & Mobile)
      this.selectCategory(0);
      document.addEventListener('keydown', this._onKey);
      // Desktop angedockt: auf Scroll schliessen (vermeidet hide-on-scroll-Versatz)
      if (this._mq.matches) window.addEventListener('scroll', this._onScroll, { passive: true });
      // Outside-click — kurze Verzögerung damit der initiale Klick nicht direkt zumacht
      setTimeout(() => document.addEventListener('click', this._onDocClick), 50);
      // Tracking-Hook (für späteren GA-Event-Einbau)
      this.dispatchEvent(new CustomEvent('doppio:shop-mega:open', { bubbles: true, detail: { anchor } }));
    }
    close() {
      this.classList.remove('is-open');
      this._open = false;
      document.removeEventListener('keydown', this._onKey);
      document.removeEventListener('click', this._onDocClick);
      window.removeEventListener('scroll', this._onScroll);
      // hide nach Animation
      setTimeout(() => { if (!this._open) this.setAttribute('hidden', ''); }, 260);
    }
    _onScroll() { if (this._open && this._mq.matches) this.close(); }
    _onKey(e) { if (e.key === 'Escape') this.close(); }
    _onDocClick(e) {
      if (this.contains(e.target)) return;
      this.close();
    }

    /* ---------- Desktop Drill-down ---------- */
    _catUrl(c) {
      return c.collection
        ? `/collections/${c.collection}`
        : `/search?q=${encodeURIComponent(c.q)}&type=product`;
    }
    _wireCats() {
      this._catEls = Array.from(this.querySelectorAll('[data-dsm-cat]'));
      this._catEls.forEach((el) => {
        const idx = parseInt(el.getAttribute('data-dsm-cat'), 10);
        const onHover = () => { if (this._mq.matches) this.selectCategory(idx); };
        el.addEventListener('mouseenter', onHover);
        el.addEventListener('focus', onHover);
        el.addEventListener('click', (e) => {
          if (this._mq.matches) return;   // Desktop: Link folgen (zur Collection/Suche)
          e.preventDefault();             // Mobile: nicht navigieren, sondern aufklappen
          this.selectCategory(idx);
        });
      });
    }
    selectCategory(idx) {
      const cat = CATEGORIES[idx];
      if (!cat) return;
      this._activeKey = cat.key;
      this._catEls.forEach((el) => {
        const on = parseInt(el.getAttribute('data-dsm-cat'), 10) === idx;
        el.classList.toggle('is-active', on);
        if (on) el.setAttribute('aria-current', 'true'); else el.removeAttribute('aria-current');
      });
      const all = this.querySelector('[data-dsm-all]');
      if (all) {
        all.href = this._catUrl(cat);
        all.textContent = `Alle ${cat.label} ansehen →`;
        all.hidden = false;
      }
      this._loadProducts(cat);
    }
    _loadProducts(cat) {
      const grid = this.querySelector('[data-dsm-grid]');
      if (!grid) return;
      if (this._cache[cat.key]) { this._renderProducts(grid, cat, this._cache[cat.key]); return; }
      grid.innerHTML = this._skeleton();
      if (this._abort) this._abort.abort();
      this._abort = (typeof AbortController !== 'undefined') ? new AbortController() : null;
      const signal = this._abort ? this._abort.signal : undefined;
      const url = cat.collection
        ? `/collections/${cat.collection}/products.json?limit=6`
        : `/search/suggest.json?q=${encodeURIComponent(cat.q)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`;
      fetch(url, { signal, headers: { 'Accept': 'application/json' } })
        .then((r) => r.ok ? r.json() : Promise.reject(r.status))
        .then((data) => {
          const items = this._normalize(cat, data);
          this._cache[cat.key] = items;
          if (this._activeKey === cat.key) this._renderProducts(grid, cat, items);
        })
        .catch((err) => {
          if (err && err.name === 'AbortError') return;
          if (this._activeKey === cat.key) this._renderProducts(grid, cat, []);
        });
    }
    _normalize(cat, data) {
      let raw = [];
      if (cat.collection && data && Array.isArray(data.products)) {
        raw = data.products.map((p) => ({
          title: p.title,
          url: '/products/' + p.handle,
          image: (p.images && p.images[0] && (p.images[0].src || p.images[0])) || p.featured_image || null,
          price: this._money(p.variants && p.variants[0] && p.variants[0].price)
        }));
      } else {
        const ps = data && data.resources && data.resources.results && data.resources.results.products;
        raw = (ps || []).map((p) => ({
          title: p.title,
          url: p.url,
          image: p.image || (p.featured_image && (p.featured_image.url || p.featured_image)) || null,
          price: this._money(p.price)
        }));
      }
      return raw.filter((p) => p.title && p.url).slice(0, 6);
    }
    // Robuster Preis-Normalizer: deckt Dezimal-Strings ("12.90"), Cent-Integers (1290)
    // und vorformatierte Strings ab. Gibt '' zurueck, wenn nicht sinnvoll parsebar.
    _money(raw) {
      if (raw == null || raw === '') return '';
      let n;
      if (typeof raw === 'number') {
        n = Number.isInteger(raw) ? raw / 100 : raw;
      } else {
        const s = String(raw).trim().replace(/[^\d.,-]/g, '');
        if (s === '') return '';
        if (/[.,]/.test(s)) n = parseFloat(s.replace(/\.(?=\d{3}\b)/g, '').replace(',', '.'));
        else n = parseInt(s, 10) / 100;
      }
      if (!isFinite(n)) return '';
      try {
        return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);
      } catch (e) { return n.toFixed(2).replace('.', ',') + ' €'; }
    }
    _sizedImg(url, w) {
      if (!url) return '';
      if (url.indexOf('//') === 0) url = 'https:' + url;
      try { const u = new URL(url, location.origin); u.searchParams.set('width', w); return u.toString(); }
      catch (e) { return url; }
    }
    _esc(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }
    _skeleton() {
      let s = '';
      for (let i = 0; i < 3; i++) {
        s += `<span class="dsm__product is-skeleton" aria-hidden="true"><span class="dsm__product-media"></span><span class="dsm__product-info"><span class="dsm__skel-line"></span><span class="dsm__skel-line dsm__skel-line--sm"></span></span></span>`;
      }
      return s;
    }
    _renderProducts(grid, cat, items) {
      if (!items || !items.length) {
        grid.innerHTML = `<p class="dsm__empty">Aktuell keine Produkte in dieser Kategorie.<br><a href="${this._catUrl(cat)}">Gesamtes Sortiment ansehen →</a></p>`;
        return;
      }
      grid.innerHTML = items.map((p) => `
        <a class="dsm__product" href="${p.url}">
          <span class="dsm__product-media">${p.image ? `<img src="${this._sizedImg(p.image, 240)}" alt="" loading="lazy" width="240" height="240">` : ''}</span>
          <span class="dsm__product-info">
            <span class="dsm__product-title">${this._esc(p.title)}</span>
            ${p.price ? `<span class="dsm__product-price">${p.price}</span>` : ''}
          </span>
        </a>`).join('');
    }
  }
  customElements.define('doppio-shop-mega', DoppioShopMega);

  // Auto-Mount: ein einziges Element ins body, sobald DOM ready
  const mount = () => {
    if (document.querySelector('doppio-shop-mega')) return;
    const el = document.createElement('doppio-shop-mega');
    document.body.appendChild(el);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }
})();

/* ============================================
   Doppio Abo-PDP-Toggle Web Component
   ============================================
   Hört auf Klick auf .dapdp__option, setzt is-active visuell,
   und falls Abo gewählt + selling_plan_id verfügbar → injiziert
   diese ID ins Impact <form is="product-form"> als hidden input.
   So funktioniert die normale Buy-Buttons-Logik (In den Warenkorb,
   Jetzt kaufen) automatisch mit Abo-Mode.
*/
(() => {
  class DoppioAboPdp extends HTMLElement {
    constructor() { super(); }
    connectedCallback() {
      if (this._mounted) return;
      this._mounted = true;
      this.hasPlans = this.getAttribute('data-has-plans') === 'true';
      this.options = Array.from(this.querySelectorAll('.dapdp__option'));
      this.options.forEach(opt => {
        opt.addEventListener('click', (e) => {
          if (opt.getAttribute('aria-disabled') === 'true') return;
          this.setActive(opt);
        });
      });
      // Wenn nur 1 Plan → hidden input enthält die ID, wird beim Abo-Klick gesetzt
      // Wenn mehrere → <select> entscheidet
      this.hiddenInput = this.querySelector('[data-dapdp-hidden-plan]');
      this.planSelect = this.querySelector('[data-dapdp-select]');
      this.plansBlock = this.querySelector('[data-dapdp-plans]');
    }
    setActive(target) {
      this.options.forEach(o => o.classList.toggle('is-active', o === target));
      const radio = target.querySelector('.dapdp__radio');
      if (radio && !radio.disabled) radio.checked = true;
      const mode = target.getAttribute('data-dapdp-mode');
      if (this.plansBlock) this.plansBlock.hidden = mode !== 'abo';
      this.syncToProductForm(mode);
      this.dispatchEvent(new CustomEvent('doppio:abo-mode', { bubbles: true, detail: { mode } }));
    }
    syncToProductForm(mode) {
      // Impact's product-form Selector
      const form = document.querySelector('form[is="product-form"], product-form form, form[action*="/cart/add"]');
      if (!form) return;
      let sp = form.querySelector('input[name="selling_plan"][data-doppio-injected]');
      if (!sp) {
        sp = document.createElement('input');
        sp.type = 'hidden';
        sp.name = 'selling_plan';
        sp.setAttribute('data-doppio-injected', '1');
        form.appendChild(sp);
      }
      if (mode === 'abo' && this.hasPlans) {
        let planId = '';
        if (this.planSelect) {
          planId = this.planSelect.value;
        } else if (this.hiddenInput) {
          planId = this.hiddenInput.getAttribute('data-plan-id') || '';
        }
        sp.value = planId;
      } else {
        sp.value = '';
      }
    }
  }
  if (!customElements.get('doppio-abo-pdp')) {
    customElements.define('doppio-abo-pdp', DoppioAboPdp);
  }
})();

/* ============================================
   PDP Buy-Buttons CSS-Leak-Cleaner
   ============================================
   Defense-in-depth: durchsucht <buy-buttons> + .product-info nach
   Text-Nodes die wie inline-CSS aussehen (#shopify-section-template...)
   und entfernt sie. Tritt z.B. auf wenn Impact's BuyButtons-WC bei
   Variant-Change innerHTML neu setzt und <style>-Inhalt durchsickert.
*/
(() => {
  const LEAK_PATTERN = /#shopify-section-|--button-background:|--button-text-color:/;
  const cleanLeaks = (root) => {
    const scope = root || document;
    const containers = scope.querySelectorAll('buy-buttons, .buy-buttons, .product-info__buy-buttons, .product-info__form');
    containers.forEach(c => {
      // Direct text-nodes
      Array.from(c.childNodes).forEach(node => {
        if (node.nodeType === Node.TEXT_NODE && LEAK_PATTERN.test(node.textContent)) {
          node.textContent = '';
        }
      });
      // Auch verschachtelte naked-text-Children in non-style/non-script Elements
      const walker = document.createTreeWalker(c, NodeFilter.SHOW_TEXT, null);
      const leaks = [];
      let n;
      while ((n = walker.nextNode())) {
        const parent = n.parentElement;
        if (!parent) continue;
        if (parent.tagName === 'STYLE' || parent.tagName === 'SCRIPT') continue;
        if (LEAK_PATTERN.test(n.textContent)) leaks.push(n);
      }
      leaks.forEach(n => n.textContent = '');
    });
  };
  const init = () => {
    cleanLeaks();
    // Re-clean nach Variant-Change (Impact dispatcht variant:change auf product-form)
    document.querySelectorAll('form[is="product-form"]').forEach(form => {
      form.addEventListener('variant:change', () => setTimeout(cleanLeaks, 50));
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', () => cleanLeaks());
})();

// Cache-bust: 1780599700

